import { useState, useEffect } from 'react'
import {
  Search,
  Plus,
  FileText,
  Layers,
  Brain,
  Edit,
  Trash2,
  Eye,
  Clock,
  Send,
  X,
} from 'lucide-react'
import { adminApi } from '../../lib/api/admin'

interface QuestionTemplate {
  id: number
  title: string
  category?: string
  target_role?: string
  tier?: string
  difficulty?: string
  questions_count?: number
  is_active: boolean
}

interface AiSummary {
  id: number
  client_name?: string
  session_date?: string
  status: string
  confidence?: number
}

export default function AdminContent() {
  const [activeTab, setActiveTab] = useState('templates')
  const [templates, setTemplates] = useState<QuestionTemplate[]>([])
  const [summaries, setSummaries] = useState<AiSummary[]>([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newTemplate, setNewTemplate] = useState({
    title: '',
    category: '',
    target_role: '',
    tier: 'standard',
    difficulty: 'medium',
  })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [questionsRes, evalsRes] = await Promise.all([
        adminApi.getQuestions(),
        adminApi.getEvaluations().catch(() => ({ data: [] })),
      ])
      setTemplates(questionsRes.data || questionsRes || [])
      setSummaries(evalsRes.data || evalsRes || [])
    } catch (err) {
      console.error('Failed to fetch content:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateTemplate = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      const created = await adminApi.createQuestion(newTemplate)
      setTemplates([created, ...templates])
      setShowCreateModal(false)
      setNewTemplate({
        title: '',
        category: '',
        target_role: '',
        tier: 'standard',
        difficulty: 'medium',
      })
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create template')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteTemplate = async (id: number) => {
    if (!window.confirm('Delete this template?')) return
    try {
      await adminApi.deleteQuestion(id)
      setTemplates(templates.filter((t) => t.id !== id))
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete template')
    }
  }

  const filteredTemplates = templates.filter((t) =>
    t.title.toLowerCase().includes(search.toLowerCase())
  )

  if (loading) {
    return <div className="loading-screen">Loading content...</div>
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Content & AI Oversight</h1>
        <p className="page-subtitle">
          Manage question templates and AI-generated content
        </p>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('templates')}
        >
          <Layers size={16} />
          Question Templates
        </button>
        <button
          className={`tab ${activeTab === 'ai-content' ? 'active' : ''}`}
          onClick={() => setActiveTab('ai-content')}
        >
          <Brain size={16} />
          AI Summaries
        </button>
      </div>

      {activeTab === 'templates' && (
        <div className="card">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 20,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flex: 1,
              }}
            >
              <Search size={18} style={{ color: 'var(--text-muted)' }} />
              <input
                type="text"
                className="form-input"
                placeholder="Search templates..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ border: 'none', boxShadow: 'none' }}
              />
            </div>
            <button
              className="btn btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus size={18} />
              New Template
            </button>
          </div>

          {filteredTemplates.length === 0 ? (
            <div className="empty-state">
              <FileText size={48} />
              <h3>No templates found</h3>
              <p>Create your first question template.</p>
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 16,
              }}
            >
              {filteredTemplates.map((t) => (
                <div
                  key={t.id}
                  style={{
                    border: '1px solid var(--border)',
                    borderRadius: 12,
                    padding: 20,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: 12,
                    }}
                  >
                    <FileText
                      size={24}
                      style={{ color: 'var(--primary-navy)' }}
                    />
                    <span
                      className={`badge ${t.is_active ? 'badge-success' : 'badge-warning'}`}
                    >
                      {t.is_active ? 'Active' : 'Draft'}
                    </span>
                  </div>
                  <h4
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      marginBottom: 4,
                    }}
                  >
                    {t.title}
                  </h4>
                  <p
                    style={{
                      fontSize: 13,
                      color: 'var(--text-muted)',
                      marginBottom: 12,
                    }}
                  >
                    {t.category || t.target_role || 'General'}
                  </p>
                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      fontSize: 12,
                      color: 'var(--text-muted)',
                      marginBottom: 12,
                    }}
                  >
                    {t.questions_count && (
                      <span>{t.questions_count} questions</span>
                    )}
                    {t.difficulty && <span>| {t.difficulty}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      className="btn btn-secondary"
                      style={{
                        padding: '6px 12px',
                        fontSize: 12,
                      }}
                    >
                      <Eye size={14} />
                      View
                    </button>
                    <button
                      className="btn btn-secondary"
                      style={{
                        padding: '6px 12px',
                        fontSize: 12,
                      }}
                    >
                      <Edit size={14} />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(t.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--danger)',
                        padding: 6,
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'ai-content' && (
        <div className="card">
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
            AI-Generated Summaries
          </h3>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Session Date</th>
                  <th>Confidence</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {summaries.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      style={{
                        textAlign: 'center',
                        padding: 40,
                        color: 'var(--text-muted)',
                      }}
                    >
                      No AI summaries yet
                    </td>
                  </tr>
                ) : (
                  summaries.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <div className="avatar">
                            {(s.client_name || 'C').charAt(0)}
                          </div>
                          <span>{s.client_name || 'Client'}</span>
                        </div>
                      </td>
                      <td>{s.session_date || '--'}</td>
                      <td>
                        {s.confidence ? (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                            }}
                          >
                            <div
                              style={{
                                width: 60,
                                height: 6,
                                background: 'var(--border)',
                                borderRadius: 3,
                                overflow: 'hidden',
                              }}
                            >
                              <div
                                style={{
                                  height: '100%',
                                  width: `${s.confidence}%`,
                                  background:
                                    s.confidence >= 90
                                      ? 'var(--success)'
                                      : s.confidence >= 80
                                        ? 'var(--warning)'
                                        : 'var(--danger)',
                                  borderRadius: 3,
                                }}
                              />
                            </div>
                            <span style={{ fontSize: 13 }}>
                              {s.confidence}%
                            </span>
                          </div>
                        ) : (
                          '--'
                        )}
                      </td>
                      <td>
                        <span
                          className={`badge ${s.status === 'draft' ? 'badge-warning' : s.status === 'approved' || s.status === 'sent' ? 'badge-success' : 'badge-primary'}`}
                        >
                          {s.status === 'draft' && (
                            <>
                              <FileText size={10} /> Draft
                            </>
                          )}
                          {s.status === 'in_review' && (
                            <>
                              <Clock size={10} /> In Review
                            </>
                          )}
                          {(s.status === 'approved' ||
                            s.status === 'sent' ||
                            s.status === 'approved_sent') && (
                            <>
                              <Send size={10} /> Sent
                            </>
                          )}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-secondary"
                          style={{
                            padding: '6px 12px',
                            fontSize: 12,
                          }}
                        >
                          Review
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Template Modal */}
      {showCreateModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowCreateModal(false)}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">New Question Template</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateTemplate}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Title</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g., Software Engineering - Senior"
                    value={newTemplate.title}
                    onChange={(e) =>
                      setNewTemplate({
                        ...newTemplate,
                        title: e.target.value,
                      })
                    }
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g., Technology, Finance"
                    value={newTemplate.category}
                    onChange={(e) =>
                      setNewTemplate({
                        ...newTemplate,
                        category: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Target Role</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g., Software Engineer"
                    value={newTemplate.target_role}
                    onChange={(e) =>
                      setNewTemplate({
                        ...newTemplate,
                        target_role: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Tier</label>
                  <select
                    className="form-input"
                    value={newTemplate.tier}
                    onChange={(e) =>
                      setNewTemplate({
                        ...newTemplate,
                        tier: e.target.value,
                      })
                    }
                  >
                    <option value="standard">Standard</option>
                    <option value="technical">Technical</option>
                    <option value="executive">Executive</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Creating...' : 'Create Template'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
